import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './LandingPage.css';
import logo from '../logo.png';

const STATS = [
  { labelKey: 'landing.stats.riders', value: '500+' },
  { labelKey: 'landing.stats.events', value: '120+' },
  { labelKey: 'landing.stats.teams', value: '40+' },
  { labelKey: 'landing.stats.countries', value: '25+' },
];

const FEATURES = [
  {
    icon: '🏆',
    titleKey: 'landing.features.competitions.title',
    descKey: 'landing.features.competitions.desc',
  },
  {
    icon: '👥',
    titleKey: 'landing.features.teams.title',
    descKey: 'landing.features.teams.desc',
  },
  {
    icon: '📊',
    titleKey: 'landing.features.stats.title',
    descKey: 'landing.features.stats.desc',
  },
  {
    icon: '🌍',
    titleKey: 'landing.features.routes.title',
    descKey: 'landing.features.routes.desc',
  },
  {
    icon: '⚡',
    titleKey: 'landing.features.realtime.title',
    descKey: 'landing.features.realtime.desc',
  },
  {
    icon: '🎯',
    titleKey: 'landing.features.ranking.title',
    descKey: 'landing.features.ranking.desc',
  },
];

const GALLERY = [
  {
    src: '/3.jpg',
    altKey: 'landing.alts.cyclingRace',
  },
  {
    src: '/41.jpeg',
    altKey: 'landing.alts.cyclistOnRoad',
  },
  {
    src: '/42.webp',
    altKey: 'landing.alts.cycling',
  },
  {
    src: '/44.png',
    altKey: 'landing.alts.cyclingTeam',
  },
  {
    src: '/43.webp',
    altKey: 'landing.alts.cyclingCompetition',
  },
  {
    src: '/4.jpg',
    altKey: 'landing.alts.professionalCyclist',
  },
];

const HOW_IT_WORKS = [
  { step: '01', titleKey: 'landing.how.step1.title', descKey: 'landing.how.step1.desc' },
  { step: '02', titleKey: 'landing.how.step2.title', descKey: 'landing.how.step2.desc' },
  { step: '03', titleKey: 'landing.how.step3.title', descKey: 'landing.how.step3.desc' },
  { step: '04', titleKey: 'landing.how.step4.title', descKey: 'landing.how.step4.desc' },
];

function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const num = parseInt(target.replace(/\D/g, ''), 10);
    if (!num) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * num));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

function StatCard({ labelKey, value, animate }) {
  const { t } = useLanguage();
  const suffix = value.replace(/\d/g, '');
  const count = useCountUp(value, 1800, animate);
  return (
    <div className="lp-stat-card">
      <div className="lp-stat-value">
        {animate ? `${count}${suffix}` : value}
      </div>
      <div className="lp-stat-label">{t(labelKey)}</div>
    </div>
  );
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="lp-root">
      {/* ── HERO ── */}
      <section className={`lp-hero ${heroLoaded ? 'lp-hero--loaded' : ''}`}>
        <div className="lp-hero-bg">
          <img
            className="lp-hero-img"
            src="/rouvy_main_page.png"
            alt={t('landing.alts.hero')}
          />
          <div className="lp-hero-overlay" />
        </div>

        <div className="lp-hero-content">
          <div className="lp-hero-logo-wrap">
            <img src={logo} alt="CVL Logo" className="lp-hero-logo" />
          </div>
          <h1 className="lp-hero-title">
            <span className="lp-hero-title-top">{t('landing.hero.title1')}</span>
            <span className="lp-hero-title-accent">{t('landing.hero.title2')}</span>
          </h1>
          <p className="lp-hero-subtitle">{t('landing.hero.subtitle')}</p>
          <div className="lp-hero-actions">
            <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
              {t('landing.hero.cta_join')}
            </button>
            <button className="lp-btn lp-btn--ghost" onClick={() => navigate('/login')}>
              {t('landing.hero.cta_login')}
            </button>
          </div>

          <div className="lp-hero-badge">
            <img
              src="/rouvy.svg"
              alt="ROUVY"
              className="lp-rouvy-badge-img"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span>{t('landing.hero.powered')}</span>
          </div>
        </div>

        <div className="lp-hero-scroll-hint">
          <div className="lp-scroll-mouse">
            <div className="lp-scroll-wheel" />
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      {/* <section className="lp-stats-section" ref={statsRef}>
        <div className="lp-container">
          <div className="lp-stats-grid">
            {STATS.map((s) => (
              <StatCard key={s.labelKey} {...s} animate={statsVisible} />
            ))}
          </div>
        </div>
      </section> */}

      {/* ── ABOUT ── */}
      <section className="lp-about-section">
        <div className="lp-container lp-about-inner">
          <div className="lp-about-text">
            <span className="lp-section-tag">{t('landing.about.tag')}</span>
            <h2 className="lp-section-title">{t('landing.about.title')}</h2>
            <p className="lp-about-desc">{t('landing.about.desc1')}</p>
            <p className="lp-about-desc">{t('landing.about.desc2')}</p>
            <button className="lp-btn lp-btn--primary lp-btn--sm" onClick={() => navigate('/signup')}>
              {t('landing.about.cta')}
            </button>
          </div>
          <div className="lp-about-img-stack">
            <img
              className="lp-about-img lp-about-img--back"
              src="/2.webp"
              alt={t('landing.alts.cycling')}
            />
            <img
              className="lp-about-img lp-about-img--front"
              src="/3.jpg"
              alt={t('landing.alts.virtualRace')}
            />
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="lp-features-section">
        <div className="lp-container">
          <span className="lp-section-tag lp-section-tag--center">{t('landing.features.tag')}</span>
          <h2 className="lp-section-title lp-section-title--center">{t('landing.features.title')}</h2>
          <div className="lp-features-grid">
            {FEATURES.map((f) => (
              <div className="lp-feature-card" key={f.titleKey}>
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">{t(f.titleKey)}</h3>
                <p className="lp-feature-desc">{t(f.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section className="lp-gallery-section">
        <div className="lp-container">
          <span className="lp-section-tag lp-section-tag--center">{t('landing.gallery.tag')}</span>
          <h2 className="lp-section-title lp-section-title--center">{t('landing.gallery.title')}</h2>
        </div>
        <div className="lp-gallery-grid">
          {GALLERY.map((img, i) => (
            <div className="lp-gallery-item" key={i}>
              <img src={img.src} alt={t(img.altKey)} loading="lazy" />
              <div className="lp-gallery-item-overlay" />
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lp-how-section">
        <div className="lp-container">
          <span className="lp-section-tag lp-section-tag--center">{t('landing.how.tag')}</span>
          <h2 className="lp-section-title lp-section-title--center">{t('landing.how.title')}</h2>
          <div className="lp-how-grid">
            {HOW_IT_WORKS.map((item, i) => (
              <div className="lp-how-card" key={i}>
                <div className="lp-how-step">{item.step}</div>
                <h3 className="lp-how-title">{t(item.titleKey)}</h3>
                <p className="lp-how-desc">{t(item.descKey)}</p>
                {i < HOW_IT_WORKS.length - 1 && <div className="lp-how-arrow">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RACE BANNER ── */}
      <section className="lp-race-banner">
        <img
          className="lp-race-banner-bg"
          src="/6.jpg"
          alt={t('landing.alts.race')}
        />
        <div className="lp-race-banner-overlay" />
        <div className="lp-race-banner-content">
          <h2 className="lp-race-banner-title">{t('landing.banner.title')}</h2>
          <p className="lp-race-banner-sub">{t('landing.banner.sub')}</p>
          <button className="lp-btn lp-btn--white" onClick={() => navigate('/signup')}>
            {t('landing.banner.cta')}
          </button>
        </div>
      </section>

      {/* ── FOOTER CTA ── */}
      <section className="lp-footer-cta">
        <div className="lp-container lp-footer-cta-inner">
          <div>
            <img src={logo} alt="CVL" className="lp-footer-logo" />
            <p className="lp-footer-tagline">{t('landing.footer.tagline')}</p>
          </div>
          <div className="lp-footer-cta-actions">
            <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
              {t('landing.hero.cta_join')}
            </button>
            <button className="lp-btn lp-btn--outline" onClick={() => navigate('/login')}>
              {t('landing.hero.cta_login')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;